// QR Kiosk v2 — docked MIDAS composer LAUNCHER (designer `KComposer`). Bar/chip
// taps open the KV2-6 fullscreen takeover (KioskMidasFullV2). Chip taps PREFILL:
// the chip's translated question is passed as initialSend and actually asked.
// Pass 1b 2026-07-24 (KV2_DESIGN_REVIEW_FEEDBACK.md §1b.4): the two static
// suggestion chips (open-reports / last-service) are REMOVED — only the
// data-grounded alarm chip remains (dormant until B4-T4 wires liveStatus).
// UUID-guarded BEFORE the affordance is ever offered (SEC-REQ-5); the takeover
// re-guards internally = double enforcement. Tokens only.
import React, { useState } from "react";
import { Machine } from "../../models/machinesModels";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Spark } from "../shared/glyphs";
import { KioskLiveStatus, isValidMachineUuid } from "./kioskLiveStatus";
import { usePressed } from "../shared/usePressed";
import { isGuestRole } from "../guest/guestSession";
import { KioskMidasFullV2 } from "../midas/KioskMidasFullV2";

export interface MidasComposerDockProps {
  machine: Machine; // route-scoped m
  liveStatus?: KioskLiveStatus; // alarm chip only — undefined until B4-T4
}

interface ChipDef {
  key: string;
  opts?: Record<string, string>;
}

export function MidasComposerDock({ machine, liveStatus }: MidasComposerDockProps) {
  const { translate } = useTranslation("public");
  const role = useAppSelector((s) => s.account.user?.role);
  const [midasOpen, setMidasOpen] = useState(false);
  const [chipSend, setChipSend] = useState<string | null>(null);
  const bar = usePressed();

  const openWithChip = (chipLabel: string) => {
    setChipSend(chipLabel);
    setMidasOpen(true);
  };
  const openBlank = () => {
    setChipSend(null);
    setMidasOpen(true);
  };

  // Interim guest-hide (mirrors MyTasksRow's guest-skip): the kiosk MIDAS bot proxy is
  // COMPANY-role-gated, so a guest tap 403s into a dead assistant. Hide the entry entirely
  // for guests until the per-company-toggle guest-MIDAS ships (deferred to a separate
  // design). Company/full users are unaffected — they still see the launcher. The takeover
  // (KioskMidasFullV2) re-guards on the same role = double enforcement.
  if (isGuestRole(role)) return null;

  // Gate BEFORE any thread-create/send can ever happen (§8.1). !midasReady →
  // render null (degrade-to-absent; no dead composer). The overlay re-guards
  // internally (v1 code, untouched) = double enforcement.
  const midasReady = isValidMachineUuid(machine.id);
  if (!midasReady) return null;

  // Alarm chip ONLY (§1b.4) — data-grounded, never speculative.
  const chips: ChipDef[] = [];
  if (liveStatus?.alarm != null) chips.push({ key: "kioskV2ChipAlarm", opts: { code: liveStatus.alarm.code } });

  return (
    <>
      <div
        style={{
          marginTop: "auto",
          flexShrink: 0,
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          background: "var(--card)",
          borderTop: "1px solid var(--border)",
          padding: "10px 14px calc(14px + env(safe-area-inset-bottom))",
        }}
      >
        {chips.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginBottom: 9, overflow: "hidden", position: "relative" }}>
            {chips.map((c) => {
              const label = translate(c.key, c.opts);
              return <ChipButton key={c.key} label={label} onClick={() => openWithChip(label)} />;
            })}
            <span
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 26,
                background: "linear-gradient(90deg, transparent, var(--card))",
              }}
            />
          </div>
        )}

        {/* Round-6 (§2.3): honest primary ACTION button — no fake text input. Full-width
            terracotta, ✦ glyph, reads as THE machine-assistant action at arm's length.
            Tapping opens the fullscreen MIDAS takeover on its welcome screen (keyboard-free). */}
        <button
          type="button"
          {...bar.pressHandlers}
          onClick={openBlank}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            height: 58,
            width: "100%",
            borderRadius: 14,
            border: "none",
            background: "var(--midas)",
            color: "var(--tier-accent-fg)",
            boxShadow: "var(--shadow)",
            fontFamily: "inherit",
            fontSize: 16.5,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            cursor: "pointer",
            ...bar.pressStyle,
          }}
        >
          <Spark size={22} />
          {translate("kioskV2MidasEntryButton")}
        </button>
      </div>

      {midasOpen && (
        <KioskMidasFullV2
          machine={machine} // UUID-guarded (§8.1) BEFORE this can mount; consumes ONLY id + model + serialNumber (+ internalNumber for create/nav)
          initialSend={chipSend ?? undefined}
          onClose={() => {
            setMidasOpen(false);
            setChipSend(null);
          }}
        />
      )}
    </>
  );
}

function ChipButton({ label, onClick }: { label: string; onClick: () => void }) {
  const press = usePressed();
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={onClick}
      style={{
        flexShrink: 0,
        height: 34,
        padding: "0 13px",
        borderRadius: 999,
        background: "var(--midas-soft)",
        border: "1px solid var(--midas-line)",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 600,
        // Legibility: pull the terracotta toward ink — raw --midas on a soft bg is too faint.
        color: "color-mix(in oklab, var(--midas) 72%, var(--ink))",
        whiteSpace: "nowrap",
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      {label}
    </button>
  );
}

export default MidasComposerDock;
